import { Dispatch, ReactNode, SetStateAction } from 'react';
import { Alert, Box, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';
import { LexicalEditor } from 'lexical';
import { useFormContext, useWatch } from 'react-hook-form';

import { CasRHFLexicalRichTextEditor, CasRHFTextField } from 'src/compass/shared/components';
import { IDataTableConfig } from 'src/compass/shared/components/CasLexicalRichTextEditor/CasPlugins/CasTableDataPlugin/CasTableDataNode';

// Direct paths, not the application barrel: this file is inside it, and importing it from itself
// is a cycle (see CasPendingEmailsPill in CasContextProviders).
import { RHFMessageRecipients } from '../RHFMessageRecipients';
import SendAsToggle from '../SendAsToggle';
import SmsTextField from '../SmsTextField';
import { RecipientOptionType, SendAsType, isChannelLocked, resolveChannel } from '../composeMode';
import BatchSmsPictures from './BatchSmsPictures';

interface Props {
  /* What this row is called - the insured name, or the customer name. */
  label: string;
  /* Where this row's To/CC pick list comes from - see RHFMessageRecipients. */
  optionsEntity: string;
  optionsParams: Record<string, string | number | undefined>;
  // The row's currently shown recipients, passed as STORED arrays: RHFMessageRecipients re-seeds
  // whenever these change identity, so fresh literals each render would wipe the user's edits.
  selectedTo: any[];
  selectedCC: any[];
  // Changes identity when this row's contacts are edited, which is what makes the To/Cc pick-list
  // refetch.
  contactsUpdate: Date | null;
  onUseDefaultChange: (useDefault: boolean) => void;
  /*
    The row is in the list but unticked, so nothing here applies to it - a channel it will not go
    out on, recipients it will not reach, a message nobody will read. Said once, plainly, instead.
  */
  excluded?: boolean;
  // This row's Send As. The channel itself is read off the live recipients below rather than
  // passed in, so an open row reflects the sender's edits before they are committed.
  sendAs: SendAsType;
  // Why the row is blocking the send, as resolved from its committed recipients.
  unresolvedReason?: string;
  onSendAsChange: (sendAs: SendAsType) => void;
  /*
    What the row's override editor can embed. SCE Reports passes its data table config, so an
    override keeps this insured's report; a customer message has no such thing and passes neither
    this nor onReadyEditor.
  */
  dataTableConfig?: IDataTableConfig;
  onReadyEditor?: Dispatch<SetStateAction<LexicalEditor | null>>;
  /*
    Shown while the row is on the default message - where SCE Reports previews this insured's data
    objects, so checking the figures does not mean turning a look into an edit.
  */
  defaultPreview?: ReactNode;
  /*
    The row goes out as a text - pinned to SMS, or on Auto with recipients that resolve to it - so
    its Custom message is edited as one: the SMS editor in place of the rich one, and no Subject.
    Decided by the dialog, with the same resolver that picks the channel it is sent on.
  */
  smsEditing?: boolean;
  // What else the SMS editor offers under the text - SCE Reports' data objects.
  smsExtras?: ReactNode;
}

export default function BatchRowPane({
  label,
  optionsEntity,
  optionsParams,
  selectedTo,
  selectedCC,
  contactsUpdate,
  onUseDefaultChange,
  excluded,
  sendAs,
  unresolvedReason,
  onSendAsChange,
  dataTableConfig,
  onReadyEditor,
  defaultPreview,
  smsEditing,
  smsExtras,
}: Props) {
  const { control } = useFormContext();
  const useDefault = useWatch({ control, name: 'useDefault', defaultValue: true });

  /*
    Resolved from the recipients as they stand in the form, not from the `channel` prop.

    The prop is computed from the row's COMMITTED recipients, and a row's edits are only committed
    when it is left - so while the row is open it describes the addresses the sender started with.
    Clearing the To then left the picker still locked to email, greying out the phone contact the
    sender had just made room for.

    These are the same two helpers the single-send window uses on its own live to/cc, so an open row
    and a single send now answer "is the channel settled yet" the same way.
  */
  const liveTo: RecipientOptionType[] = useWatch({ control, name: 'to' }) ?? [];
  const liveCC: RecipientOptionType[] = useWatch({ control, name: 'cc' }) ?? [];

  const liveChannel = resolveChannel(sendAs, liveTo, liveCC);
  const liveChannelLocked = isChannelLocked(sendAs, liveTo, liveCC);

  // The row's name and the Revert link live in the pane header the dialog owns, so they can share
  // a row with the prev/next navigator.
  return (
    <>
      {excluded && (
        <Typography variant="subtitle2" sx={{ color: 'error.dark', py: 1 }}>
          Message will be skipped
        </Typography>
      )}

      {/*
        Hidden rather than unmounted, like the two panes the dialog owns: the override editor below
        is a Lexical instance that reads its initial state once at mount, so unmounting it when a
        row is unticked would rebuild it - and lose the body - every time one is.
      */}
      <Box sx={{ display: excluded ? 'none' : undefined }}>
        {/*
          This row's own channel. Auto reads it off the recipients below, exactly as the compose
          window does for a single send; Email or SMS pins it, which is how a row whose contacts
          cannot agree gets settled without editing anybody's contact details.
        */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ pb: 1 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Send as
          </Typography>
          <SendAsToggle value={sendAs} onChange={onSendAsChange} />
        </Stack>

        {unresolvedReason && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            {unresolvedReason}
          </Alert>
        )}

        {/*
          Locked whenever the channel is settled, which is what greys out the contacts this message
          could not reach. While it is unresolved nothing is greyed out - the sender is still choosing,
          and hiding half the options is no way to help them choose.
        */}
        <RHFMessageRecipients
          optionsEntity={optionsEntity}
          optionsParams={optionsParams}
          selectedTo={selectedTo}
          selectedCC={selectedCC}
          update={contactsUpdate}
          channel={liveChannel}
          channelLocked={liveChannelLocked}
          // A text has no CC of its own, so it is only kept on screen while it still holds someone.
          showCC={liveChannel !== 'sms' || (selectedCC?.length ?? 0) > 0}
        />

        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={!!useDefault}
              onChange={(e) => onUseDefaultChange(e.target.checked)}
            />
          }
          label="Use default message"
        />

        {useDefault && defaultPreview}

        {/*
          The override editor is mounted for EVERY row, not only customized ones, and merely hidden
          while "use default" is checked. Lexical reads its initial state once at mount, so the body
          has to be pushed in imperatively when the selection changes - a conditional mount here would
          rebuild the whole editor on every list click.
        */}
        <div style={{ display: useDefault ? 'none' : 'block' }}>
          {/* A text has no subject, and this row goes out on one channel. */}
          {!smsEditing && <CasRHFTextField name="rowSubject" label="Subject" />}
          {/*
            Still the row's message while it is edited as a text - hidden, not unmounted, for the
            reason above. The dialog carries what the SMS editor holds back into it.
          */}
          <Box sx={{ display: smsEditing ? 'none' : undefined }}>
            <CasRHFLexicalRichTextEditor
              mode="advanced"
              name="rowContent"
              label="Body"
              helperText={`Sent only to ${label}`}
              dataTableConfig={dataTableConfig}
              onReadyEditor={onReadyEditor}
            />
          </Box>
          {smsEditing && (
            <>
              <SmsTextField name="rowTextContent" picturesName="rowSmsImages" />
              <BatchSmsPictures name="rowSmsImages" />
              {smsExtras}
            </>
          )}
        </div>
      </Box>
    </>
  );
}
